import React, { useState, useCallback } from 'react'
import { Table } from 'antd'
import './CommonTable.css'

// Custom header cell that renders a drag handle for column resizing
const ResizableTitle = (props) => {
  const { onResize, width, ...restProps } = props

  if (!onResize) {
    return <th {...restProps} />
  }

  const handleMouseDown = (e) => {
    e.preventDefault()
    e.stopPropagation()

    const startX = e.clientX
    const startWidth = width ?? e.target.closest('th').offsetWidth

    const handleMouseMove = (moveEvent) => {
      const newWidth = Math.max(60, startWidth + (moveEvent.clientX - startX))
      onResize(newWidth)
    }

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  return (
    <th {...restProps}>
      {restProps.children}
      <span
        className="common-table-resize-handle"
        onMouseDown={handleMouseDown}
        onClick={(e) => e.stopPropagation()}
      />
    </th>
  )
}

const CommonTable = ({
  columns,
  dataSource,
  rowKey,
  pagination = { pageSize: 10 },
  loading = false,
  size = 'middle',
  scroll,
  ...restProps
}) => {
  // Track resized widths per column, keyed by column key/dataIndex/index
  const [columnWidths, setColumnWidths] = useState({})

  const getColKey = useCallback(
    (col, index) => col.key ?? col.dataIndex ?? index,
    []
  )

  const handleResize = useCallback(
    (colKey) => (newWidth) => {
      setColumnWidths((prev) => ({ ...prev, [colKey]: newWidth }))
    },
    []
  )

  // Convert a column width (number or "%") into a concrete pixel value
  const toPixelWidth = useCallback((width) => {
    if (typeof width === 'number') return width
    if (typeof width === 'string' && width.endsWith('%')) {
      return Math.round((parseFloat(width) / 100) * 1200)
    }
    return 150
  }, [])

  // Add sorter, ellipsis and resize handlers to all columns
  const enhancedColumns = columns.map((col, index) => {
    const colKey = getColKey(col, index)
    const width = columnWidths[colKey] ?? toPixelWidth(col.width)

    return {
      ellipsis: true,
      sorter: col.sorter !== false ? (a, b) => {
        const aVal = a[col.dataIndex]
        const bVal = b[col.dataIndex]

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return aVal - bVal
        }

        return (aVal || '').toString().localeCompare((bVal || '').toString())
      } : undefined,
      ...col,
      width,
      onHeaderCell: (column) => ({
        width: typeof column.width === 'number' ? column.width : undefined,
        onResize: handleResize(colKey),
      }),
    }
  })

  // Total width drives the horizontal scrollbar when columns are resized wider
  const totalWidth = enhancedColumns.reduce((sum, col) => sum + col.width, 0)

  return (
    <Table
      className="common-table"
      components={{ header: { cell: ResizableTitle } }}
      columns={enhancedColumns}
      dataSource={dataSource}
      rowKey={rowKey}
      pagination={pagination}
      loading={loading}
      size={size}
      scroll={{ ...scroll, x: totalWidth }}
      bordered
      {...restProps}
    />
  )
}

export default CommonTable
